import React from 'react';
import { Paper, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { FileRequestAction, FileRequestStatus, FileRequestTarget } from '@moderepo/mode-apis';
import { useModePanelStyle, useModeFormStyle, BaseEntityField, BaseInfoCompProps, InfoCompPanelHeader, ModeInfoCompTextInputField }
    from '@moderepo/mode-app-components';


const useStyle = makeStyles(() => {
    return {
        root: {

        },
    };
}, { name: 'RCFileRequestInfo' });



export interface RCFileRequestInfoProps extends BaseInfoCompProps {
    readonly fields: {
        readonly id: BaseEntityField<number>;
        readonly homeId: BaseEntityField<number>;
        readonly deviceId: BaseEntityField<number>;
        readonly robotId?: BaseEntityField<string> | undefined;
        readonly robotClass?: BaseEntityField<string> | undefined;
        readonly target: BaseEntityField<FileRequestTarget>;
        readonly action: BaseEntityField<FileRequestAction>;
        readonly errorMessage?: BaseEntityField<string> | undefined;
        readonly createdAt: BaseEntityField<string>;
        readonly updatedAt: BaseEntityField<string>;
        readonly expirationDate?: BaseEntityField<string> | undefined;
        readonly status: BaseEntityField<FileRequestStatus>;
        readonly downloadRequestParameters?: {
            readonly fileType: BaseEntityField<string>;
            readonly begin: BaseEntityField<string>;
            readonly end: BaseEntityField<string>;
        } | undefined;
        readonly uploadRequestParameters?: {
            readonly fileName: BaseEntityField<string>;
            readonly fileSetSize: BaseEntityField<number>;
            readonly fileSetChecksum: BaseEntityField<string>;
        } | undefined;

        readonly downloadRequestResults?: {
            readonly fileSetSize: BaseEntityField<number>;
            readonly fileSetChecksum: BaseEntityField<string>;
        } | undefined;
    };

    readonly texts: {
        readonly requestMetadataTitle: string;
        readonly requestResultsTitle: string;
    }
}



/**
 * This is a DUMB component used for displaying File Request info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCFileRequestInfo: React.FC<RCFileRequestInfoProps> = (props: RCFileRequestInfoProps) => {
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, classes.root)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                >
                    <div className={clsx(formClasses.formInputGroup)}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.id}
                            fieldName="id"
                            inputValue={props.fields.id.displayValue ?? props.fields.id.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.status}
                            fieldName="status"
                            inputValue={props.fields.status.displayValue ?? props.fields.status.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.homeId}
                            fieldName="homeId"
                            inputValue={props.fields.homeId.displayValue ?? props.fields.homeId.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.deviceId}
                            fieldName="deviceId"
                            inputValue={props.fields.deviceId.displayValue ?? props.fields.deviceId.value}
                        />
                        {props.fields.robotId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.robotId}
                                fieldName="robotId"
                                inputValue={props.fields.robotId.displayValue ?? props.fields.robotId.value}
                            />
                        )}
                        {props.fields.robotClass && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.robotClass}
                                fieldName="robotClass"
                                inputValue={props.fields.robotClass.displayValue ?? props.fields.robotClass.value}
                            />
                        )}
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.target}
                            fieldName="target"
                            inputValue={props.fields.target.displayValue ?? props.fields.target.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.action}
                            fieldName="action"
                            inputValue={props.fields.action.displayValue ?? props.fields.action.value}
                        />
                        {props.fields.errorMessage && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                multiline
                                compProps={props}
                                inputProps={props.fields.errorMessage}
                                fieldName="errorMessage"
                                inputValue={props.fields.errorMessage.displayValue ?? props.fields.errorMessage.value}
                            />
                        )}

                        {props.fields.downloadRequestParameters && (
                            <div className={formClasses.formInputSection}>
                                <div className={formClasses.formInputSectionTitle}>
                                    {props.texts.requestMetadataTitle}
                                </div>
                                <div className={formClasses.formInputSectionContent}>
                                    <div className={formClasses.formInputGroup}>
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.downloadRequestParameters.fileType}
                                            fieldName="downloadRequestParameters.fileType"
                                            inputValue={props.fields.downloadRequestParameters.fileType.displayValue
                                                ?? props.fields.downloadRequestParameters.fileType.value}
                                        />
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.downloadRequestParameters.begin}
                                            fieldName="downloadRequestParameters.begin"
                                            inputValue={props.fields.downloadRequestParameters.begin.displayValue
                                                ?? props.fields.downloadRequestParameters.begin.value}
                                        />
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.downloadRequestParameters.end}
                                            fieldName="downloadRequestParameters.end"
                                            inputValue={props.fields.downloadRequestParameters.end.displayValue
                                                ?? props.fields.downloadRequestParameters.end.value}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {props.fields.uploadRequestParameters && (
                            <div className={formClasses.formInputSection}>
                                <div className={formClasses.formInputSectionTitle}>
                                    {props.texts.requestMetadataTitle}
                                </div>
                                <div className={formClasses.formInputSectionContent}>
                                    <div className={formClasses.formInputGroup}>
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.uploadRequestParameters.fileName}
                                            fieldName="uploadRequestParameters.fileName"
                                            inputValue={props.fields.uploadRequestParameters.fileName.displayValue
                                                    ?? props.fields.uploadRequestParameters.fileName.value}
                                        />
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.uploadRequestParameters.fileSetSize}
                                            fieldName="uploadRequestParameters.fileSetSize"
                                            inputValue={props.fields.uploadRequestParameters.fileSetSize.displayValue
                                                ?? props.fields.uploadRequestParameters.fileSetSize.value}
                                        />
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            multiline
                                            compProps={props}
                                            inputProps={props.fields.uploadRequestParameters.fileSetChecksum}
                                            fieldName="uploadRequestParameters.fileSetChecksum"
                                            inputValue={props.fields.uploadRequestParameters.fileSetChecksum.displayValue
                                                ?? props.fields.uploadRequestParameters.fileSetChecksum.value}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {props.fields.downloadRequestResults && (
                            <div className={formClasses.formInputSection}>
                                <div className={formClasses.formInputSectionTitle}>
                                    {props.texts.requestResultsTitle}
                                </div>
                                <div className={formClasses.formInputSectionContent}>
                                    <div className={formClasses.formInputGroup}>
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            compProps={props}
                                            inputProps={props.fields.downloadRequestResults.fileSetSize}
                                            fieldName="downloadRequestResults.fileSetSize"
                                            inputValue={props.fields.downloadRequestResults.fileSetSize.displayValue
                                                ?? props.fields.downloadRequestResults.fileSetSize.value}
                                        />
                                        <ModeInfoCompTextInputField
                                            className={formClasses.formInputField}
                                            inputType="text"
                                            multiline
                                            compProps={props}
                                            inputProps={props.fields.downloadRequestResults.fileSetChecksum}
                                            fieldName="downloadRequestResults.fileSetChecksum"
                                            inputValue={props.fields.downloadRequestResults.fileSetChecksum.displayValue
                                                    ?? props.fields.downloadRequestResults.fileSetChecksum.value}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.createdAt}
                            fieldName="createdAt"
                            inputValue={props.fields.createdAt.displayValue ?? props.fields.createdAt.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.updatedAt}
                            fieldName="updatedAt"
                            inputValue={props.fields.updatedAt.displayValue ?? props.fields.updatedAt.value}
                        />
                        {props.fields.expirationDate && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.expirationDate}
                                fieldName="expirationDate"
                                inputValue={props.fields.expirationDate.displayValue ?? props.fields.expirationDate.value}
                            />
                        )}
                    </div>
                </form>
            </div>

        </Paper>
    );

};
